/**
 * How the 笔记 view lays a kind out when it declares a group: by one frontmatter field, each group titled
 * and ordered by others, the notes in it ordered by their own field (ADR-0042). Plain .ts so `node --test`
 * can load it.
 */
import type { NoteIndexEntry, NoteKindView } from "../../../shared/wire/notes.ts";

export interface NoteGroup {
	key: string;
	label: string;
	entries: NoteIndexEntry[];
}

/** Numbers as numbers, so `10` comes after `2`. */
const compare = (a: string, b: string) => a.localeCompare(b, "zh", { numeric: true });

/** The grouped kinds in their groups, and every other note after them in the order it came. */
export function groupNotes(entries: readonly NoteIndexEntry[], kinds: readonly NoteKindView[]): { groups: NoteGroup[]; rest: NoteIndexEntry[] } {
	const byName = new Map(kinds.map((k) => [k.name, k]));
	const groups = new Map<string, { group: NoteGroup; order: string }>();
	const rest: NoteIndexEntry[] = [];
	for (const e of entries) {
		const kind = byName.get(e.kind);
		if (!kind?.group) {
			rest.push(e);
			continue;
		}
		const key = e.fields?.[kind.group.by] ?? "";
		const id = `${e.kind}\u0000${key}`;
		if (!groups.has(id)) {
			const label = (kind.group.label && e.fields?.[kind.group.label]) || key || "未分组";
			groups.set(id, { group: { key, label, entries: [] }, order: (kind.group.order && e.fields?.[kind.group.order]) || label });
		}
		groups.get(id)!.group.entries.push(e);
	}
	const ordered = [...groups.values()].sort((a, b) => compare(a.order, b.order)).map(({ group }) => group);
	for (const group of ordered) {
		const kind = byName.get(group.entries[0]!.kind)!;
		const field = kind.order;
		group.entries.sort((a, b) => (field ? compare(a.fields?.[field] ?? "", b.fields?.[field] ?? "") : 0) || compare(a.title, b.title));
	}
	return { groups: ordered, rest };
}
